import React, { FC, useMemo } from "react";
import clsx from "clsx";

import styles from "../december-doors.module.scss";

const BOKEH_COUNT = 72;
const GLITTER_COUNT = 170;
const TINTS = ["gold", "gold", "gold", "gold", "lilac", "white"] as const;

/** Deterministic PRNG so the sky is the same on every render and in tests. */
const mulberry32 = (seed: number) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

interface Particle {
  style: React.CSSProperties;
  tint: (typeof TINTS)[number];
  isBright?: boolean;
}

const makeSky = () => {
  const random = mulberry32(2026_12_01);
  const between = (lo: number, hi: number) => lo + random() * (hi - lo);

  const bokeh: Particle[] = Array.from({ length: BOKEH_COUNT }, (_, index) => {
    // Every ninth light is large and bright, so the drift is unmistakable.
    const isBright = index % 9 === 0;
    const size = isBright ? between(44, 76) : between(8, 50);
    return {
      tint: isBright ? "gold" : TINTS[Math.floor(random() * TINTS.length)],
      isBright,
      style: {
        "--x": `${between(-4, 100).toFixed(2)}%`,
        "--y": `${between(0, 100).toFixed(2)}%`,
        "--size": `${size.toFixed(1)}px`,
        "--blur": `${(size / 9).toFixed(1)}px`,
        "--o": (isBright ? between(0.55, 0.85) : between(0.25, 0.75)).toFixed(2),
        "--x1": `${between(-160, 160).toFixed(0)}px`,
        "--x2": `${between(-160, 160).toFixed(0)}px`,
        "--x3": `${between(-160, 160).toFixed(0)}px`,
        "--dx": `${between(-120, 120).toFixed(0)}px`,
        "--dur": `${between(28, 64).toFixed(1)}s`,
        "--twinkle": `${between(2.2, 4.8).toFixed(1)}s`,
        "--delay": `${between(-64, 0).toFixed(1)}s`,
      } as React.CSSProperties,
    };
  });

  const glitter: Particle[] = Array.from({ length: GLITTER_COUNT }, () => ({
    tint: random() > 0.35 ? "gold" : "white",
    style: {
      "--x": `${between(0, 100).toFixed(2)}%`,
      "--y": `${between(0, 100).toFixed(2)}%`,
      "--size": `${between(1.5, 3.2).toFixed(1)}px`,
      "--o": between(0.35, 0.95).toFixed(2),
      "--x1": `${between(-50, 50).toFixed(0)}px`,
      "--x2": `${between(-50, 50).toFixed(0)}px`,
      "--x3": `${between(-50, 50).toFixed(0)}px`,
      "--dx": `${between(-30, 30).toFixed(0)}px`,
      "--dur": `${between(36, 80).toFixed(1)}s`,
      "--twinkle": `${between(1.4, 3.6).toFixed(1)}s`,
      "--delay": `${between(-80, 0).toFixed(1)}s`,
    } as React.CSSProperties,
  }));

  return { bokeh, glitter };
};

/**
 * The page's night sky: a deep plum field with slow-drifting gold and lilac
 * bokeh and a scatter of fine glitter, drawn in CSS so nothing ships as an
 * asset. Purely decorative (aria-hidden); every movement stops under
 * prefers-reduced-motion.
 */
const NightSky: FC = () => {
  const { bokeh, glitter } = useMemo(makeSky, []);
  return (
    <div className={styles.sky} aria-hidden data-testid="night-sky">
      <div className={clsx(styles["sky-glow"], styles["sky-glow-top"])} />
      <div className={clsx(styles["sky-glow"], styles["sky-glow-mid"])} />
      <div className={clsx(styles["sky-glow"], styles["sky-glow-low"])} />
      {bokeh.map((particle, index) => (
        <span
          key={`b${index}`}
          className={clsx(
            styles.bokeh,
            styles[`bokeh-${particle.tint}`],
            particle.isBright && styles["bokeh-bright"]
          )}
          style={particle.style}
        />
      ))}
      {glitter.map((particle, index) => (
        <span
          key={`g${index}`}
          className={clsx(styles.glitter, styles[`glitter-${particle.tint}`])}
          style={particle.style}
        />
      ))}
    </div>
  );
};

export default NightSky;
